/**
 * The request form's questions and answers, pure. The wording mirrors the Airtable form's fields;
 * what Personio or the login already knows (the employee's name, email and dates, the supervisor)
 * is not asked, and the template is derived by the proxy.
 */
import type { EarlierRequest, FormPerson, FormSubmission, GradeKey } from '../../types/referenceLetterForm';
import { formatDate } from '../referenceLetters/format';

export const MID_TERM = 'Mid-term Reference Letter';

/** The graded questions, in the order the letter reads them; Leadership only for someone who leads people. */
export const GRADE_QUESTIONS: Array<{ key: GradeKey; label: string; hint: string }> = [
  { key: 'expertise', label: 'Expertise', hint: 'Knowledge and skills for the role' },
  { key: 'apprehension', label: 'Apprehension', hint: 'How quickly they understand new things' },
  { key: 'motivation', label: 'Motivation', hint: 'Commitment and initiative' },
  { key: 'workingStyle', label: 'Working style', hint: 'Reliability, care and independence' },
  { key: 'behaviour', label: 'Behaviour', hint: 'Towards colleagues, supervisors and partners' },
  { key: 'workPerformance', label: 'Work performance', hint: 'Quality and amount of results' },
  { key: 'leadership', label: 'Leadership', hint: 'Leading and developing their team' },
  { key: 'overallRating', label: 'Overall rating', hint: 'Sets the closing thanks of the letter' },
];

/** How a choice reads on the form; the value sent stays Airtable's own. */
export const CHOICE_LABELS: Record<string, string> = {
  'End-of-employment Reference Letter': 'End of employment',
  'Mid-term Reference Letter': 'Interim — still employed',
  'Very Good': 'Very good',
};
/** The readable label of an Airtable choice; one without a label of its own reads as itself. */
export const choiceLabel = (value: string) => CHOICE_LABELS[value] ?? value;

export interface Answers {
  employeeId: string | null;
  type: string | null;
  language: string | null;
  /** The exit reason for a final letter, the interim reason for an interim one. */
  reason: string | null;
  isLeader: string | null;
  grades: Partial<Record<GradeKey, string>>;
  tasks: string;
}

/** A fresh form, optionally with the person a link pre-selected. */
export const emptyAnswers = (employeeId: string | null = null): Answers => ({
  employeeId, type: null, language: null, reason: null, isLeader: null, grades: {}, tasks: '',
});

/** The grade questions this request asks: Leadership only when the employee leads people. */
export const gradeQuestions = (isLeader: string | null) => GRADE_QUESTIONS.filter((q) => q.key !== 'leadership' || isLeader === 'Yes');

/**
 * The request body. A grade not asked (Leadership for a non-leader) is not sent. `updates` is the
 * earlier, partial request the form showed (and Send updates), or null for a new one.
 */
export function toSubmission(a: Answers, updates: FormSubmission['updates'] = null): FormSubmission {
  const asked = new Set(gradeQuestions(a.isLeader).map((q) => q.key));
  const grades = Object.fromEntries(Object.entries(a.grades).filter(([k]) => asked.has(k as GradeKey)));
  return {
    employeeId: a.employeeId ?? '',
    type: a.type ?? '',
    language: a.language ?? '',
    ...(a.type === MID_TERM ? { midTermReason: a.reason ?? '' } : { exitReason: a.reason ?? '' }),
    isLeader: a.isLeader ?? '',
    grades,
    tasks: a.tasks,
    updates,
  };
}

/**
 * The answers once the earlier request for the chosen person and type has loaded (David, Oct 13
 * 2026). A newly found request REPLACES the answers below the person: whatever was typed for
 * someone else must not reach this person's request under a note saying it is what was sent before.
 * Leaving a prefilled request (another person or type, with no earlier request) empties what it
 * filled. The same request found again (the other type and back) leaves the answers, edits included.
 */
export function withEarlier(a: Answers, earlier: EarlierRequest | null, previous: EarlierRequest | null): Answers {
  if (earlier?.id === previous?.id) return a;
  const blank = { ...a, language: null, reason: null, isLeader: null, grades: {}, tasks: '' };
  if (!earlier) return blank;
  return { ...blank, language: earlier.language, reason: earlier.reason, isLeader: earlier.isLeader, grades: { ...earlier.grades }, tasks: earlier.tasks };
}

/**
 * Whether the answers that choose the letter's template are in — the type, the person, the language
 * and "leads people?" — so the grades' sentences can be shown. Until then the grade and task steps
 * are locked (David, Oct 13 2026: a step that silently waits on earlier ones is not intuitive).
 */
export const templateReady = (a: Answers) => Boolean(a.type && a.employeeId && a.language && a.isLeader);

/**
 * How much of the form is answered, for the send bar: the type, the person, the language, the
 * reason, "leads people?", every grade asked, and the task notes. What the proxy requires, counted
 * the same way — the bar cannot say "all answered" while Send would be refused for a blank answer.
 */
export function formProgress(a: Answers): { done: number; total: number } {
  const grades = gradeQuestions(a.isLeader).map((q) => a.grades[q.key]);
  const answered = [a.type, a.employeeId, a.language, a.reason, a.isLeader, ...grades, a.tasks.trim()];
  return { done: answered.filter(Boolean).length, total: answered.length };
}

/**
 * Whom the picker offers for a type of letter (David, Oct 13 2026): an interim letter is for someone
 * still employed, any other for someone whose employment ends (a termination, or a fixed-term
 * contract running out) — no type chosen, nobody yet. Direct
 * reports come first, each group in the proxy's own (name) order.
 */
export function candidatesFor(people: FormPerson[], type: string | null): FormPerson[] {
  if (!type) return [];
  const fits = people.filter((p) => (type === MID_TERM ? p.current : Boolean(p.endDate)));
  return [...fits.filter((p) => p.direct), ...fits.filter((p) => !p.direct)];
}

/**
 * The type of letter a link's person most likely needs: end of employment for a leaver, interim for
 * someone still employed. `null` when the vocabulary has no such choice.
 */
export function letterTypeFor(person: FormPerson, types: string[]): string | null {
  if (!person.endDate) return types.includes(MID_TERM) ? MID_TERM : null;
  return types.find((t) => t !== MID_TERM) ?? null;
}

/** "Working Student · 1 Oct 2025 – 30 Sep 2026": the line that tells two contracts of one person apart. */
export const contractLine = (p: FormPerson) =>
  [p.position, `${formatDate(p.hireDate)} – ${p.endDate ? formatDate(p.endDate) : 'today'}`].filter(Boolean).join(' · ');
